import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Menu, Home, Search, Heart, MessageCircle, User, Shield, Building2 } from "lucide-react";

export default function Navbar({ user, onAuthClick, onLogout }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isAdmin = user?.role === "admin";

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/properties", label: "Properties" },
    { href: "/map", label: "Map" },
    { href: "/about", label: "About" },
    { href: "/contact", label: "Contact" },
  ];

  return (
    <>
      <header data-testid="main-navbar" className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isHome ? "glass-dark" : "glass shadow-sm"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-18">
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-8 h-8 bg-terracotta rounded-lg flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <span className={`font-heading font-bold text-xl ${isHome ? "text-white" : "text-slate-navy"}`}>
                Slab<span className="text-terracotta">Story</span>
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link key={link.href} to={link.href} data-testid={`nav-${link.label.toLowerCase()}`}
                  className={`text-sm font-medium transition-colors hover:text-terracotta ${location.pathname === link.href ? "text-terracotta" : isHome ? "text-white/80" : "text-slate-600"}`}>
                  {link.label}
                </Link>
              ))}
            </nav>

            <div className="hidden md:flex items-center gap-3">
              {isAdmin && (
                <Link to="/admin">
                  <Button data-testid="admin-dashboard-btn" variant="outline" className={`rounded-xl text-sm ${isHome ? "border-white/30 text-white hover:bg-white/10" : ""}`}>
                    <Shield className="w-4 h-4 mr-1.5" /> Admin
                  </Button>
                </Link>
              )}
              <Link to="/contact">
                <Button data-testid="list-property-btn" className="bg-terracotta hover:bg-terracotta-hover text-white rounded-xl text-sm font-semibold px-5">
                  List With Us
                </Button>
              </Link>
              {user ? (
                <div className="flex items-center gap-2">
                  <Link to="/saved">
                    <Button variant="ghost" data-testid="saved-btn" className={`rounded-xl ${isHome ? "text-white hover:bg-white/10" : ""}`}>
                      <Heart className="w-4 h-4 mr-1.5" /> Saved
                    </Button>
                  </Link>
                  <Button variant="ghost" size="sm" onClick={onLogout} data-testid="logout-btn"
                    className={`text-xs rounded-xl ${isHome ? "text-white/70 hover:text-white hover:bg-white/10" : "text-slate-500"}`}>
                    Logout
                  </Button>
                </div>
              ) : (
                <Button variant="outline" onClick={onAuthClick} data-testid="signin-btn"
                  className={`rounded-xl text-sm ${isHome ? "border-white/30 text-white hover:bg-white/10" : "border-slate-200"}`}>
                  Sign In
                </Button>
              )}
            </div>

            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                  <Menu className={`w-5 h-5 ${isHome ? "text-white" : "text-slate-navy"}`} />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-0">
                <div className="p-6 space-y-6">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-terracotta rounded-lg flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-white" />
                    </div>
                    <span className="font-heading font-bold text-xl text-slate-navy">Slab<span className="text-terracotta">Story</span></span>
                  </div>
                  <nav className="space-y-1">
                    {navLinks.map((link) => (
                      <Link key={link.href} to={link.href} onClick={() => setMobileOpen(false)}
                        className={`block px-4 py-3 rounded-xl text-sm font-medium transition-colors ${location.pathname === link.href ? "bg-terracotta/10 text-terracotta" : "text-slate-600 hover:bg-slate-50"}`}>
                        {link.label}
                      </Link>
                    ))}
                    {isAdmin && (
                      <Link to="/admin" onClick={() => setMobileOpen(false)} className="block px-4 py-3 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-50">
                        <Shield className="w-4 h-4 inline mr-2" /> Admin Dashboard
                      </Link>
                    )}
                  </nav>
                  {user ? (
                    <div className="space-y-2 pt-4 border-t">
                      <Button variant="ghost" className="w-full rounded-xl justify-start text-red-500" onClick={() => { onLogout(); setMobileOpen(false); }}>Logout</Button>
                    </div>
                  ) : (
                    <Button className="w-full bg-slate-navy hover:bg-slate-800 text-white rounded-xl" onClick={() => { onAuthClick(); setMobileOpen(false); }}>Sign In</Button>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <nav data-testid="mobile-bottom-nav" className="md:hidden fixed bottom-0 left-0 right-0 z-50 glass border-t border-slate-200 px-2 py-1">
        <div className="flex items-center justify-around">
          {[
            { icon: Home, label: "Home", href: "/" },
            { icon: Search, label: "Search", href: "/properties" },
            { icon: Heart, label: "Saved", href: "/saved" },
            { icon: MessageCircle, label: "Contact", href: "/contact" },
            { icon: User, label: "Profile", href: user ? "/saved" : "#", onClick: user ? undefined : onAuthClick },
          ].map((item) => (
            <Link key={item.label} to={item.href} onClick={item.onClick} data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              className={`flex flex-col items-center py-1.5 px-3 rounded-lg transition-colors ${location.pathname === item.href ? "text-terracotta" : "text-slate-400 hover:text-slate-600"}`}>
              <item.icon className="w-5 h-5" />
              <span className="text-[10px] mt-0.5 font-medium">{item.label}</span>
            </Link>
          ))}
        </div>
      </nav>
    </>
  );
}
